<template>
  <div>
    <!-- STEPS FOR ORDER -->
    <div class="row">
      <div class="col-md-9">
        <h3 class="font-weight-bold bp_color text-uppercase">Create a new client confirmation order</h3>
        <p>Use the following form to take an order for labour from </p>
      </div>
      <div class="col-md-3">
        <button class="btn orcaBtn grey_bg" @click.prevent="reset_order">Reset Order</button>
      </div>
    </div>

    <div class="order_steps" v-for="(step, key) in steps">

      <div class="step" :class="[step.active ? 'step_active' : '', step.completed ? 'step_completed' : '', step.errors ? 'step_errors' : '']">
        <div @click="select_step(key, step.active)" class="step_head">
          <div class="step_icon"><i :class="step.icon"></i></div>
          <div class="step_text">{{ step.text }}</div>
        </div>

        <div class="step_content">
          <component :is="step.component_name" v-if="step.active" :order_action="'create'"></component>
        </div>
      </div>
    </div>

    <div v-if="$store.state.orders.order_steps.publish_information.completed">
      <button @click.prevent="save_order" class="btn orcaBtn w-100" :disabled="saving">
        <span v-if="saving" class="btnLoading"><i class="fas fa-spinner fa-pulse"></i>Saving ...</span>
        <span v-else>Save Order</span>
      </button>
    </div>
  </div>
</template>
<script>
// import all order components dynamically for inclusion
let orderComponents = {};
let context = require.context('./steps', true, /\.vue$/);
context.keys().forEach(function (key) {
  let componentName2 = key.match(/\/([^\/]+?)\.vue$/)[1];
  orderComponents[componentName2] = context(key).default;
});

export default {
  name: 'create_order',
  components: orderComponents,
  data() {
    return {
      current_step: 0,
      steps: {
        basic_info: this.$store.state.orders.order_steps.basic_info,
        job_locations: this.$store.state.orders.order_steps.job_locations,
        positions_rates: this.$store.state.orders.order_steps.positions_rates,
        timesheet_authorisation: this.$store.state.orders.order_steps.timesheet_authorisation,
        compliance_terms: this.$store.state.orders.order_steps.compliance_terms,
        publish_information: this.$store.state.orders.order_steps.publish_information,
        review_order: this.$store.state.orders.order_steps.review_order,
      },
      saving: false,
      orders: [],
    };
  },
  methods: {
    // ---------- error helpers ----------
    _extractErrorLines(err) {
      const lines = [];
      const r = err?.response;
      const data = r?.data;

      if (typeof data === 'string') {
        lines.push(data);
      } else if (data && typeof data === 'object') {
        if (typeof data.message === 'string') lines.push(data.message);

        // prefer Laravel errors bag if present
        const bag = data.errors && typeof data.errors === 'object' ? data.errors : null;
        const fieldsObj = bag || data; // sometimes validation fields are at root

        Object.keys(fieldsObj || {}).forEach(k => {
          const v = fieldsObj[k];
          if (Array.isArray(v)) v.forEach(msg => lines.push(`${k}: ${String(msg)}`));
          else if (typeof v === 'string') lines.push(`${k}: ${v}`);
        });
      } else if (err?.message) {
        lines.push(err.message);
      }

      if (lines.length === 0) {
        lines.push('Network or server error.');
        if (r?.status) lines.push(`HTTP ${r.status}`);
      }

      return Array.from(new Set(lines)).slice(0, 12);
    },

    _showErrorAlert(err, title = 'Order not saved') {
      const lines = this._extractErrorLines(err);
      const html = `
        <div class="snotifyToast__title">${title}</div>
        <div class="snotifyToast__body">
          <div style="max-height:220px; overflow:auto;">
            <ul style="margin:0; padding-left:18px;">
              ${lines.map(l => `<li>${this.$sanitize ? this.$sanitize(l) : l}</li>`).join('')}
            </ul>
          </div>
        </div>
      `;
      this.$snotify?.html(html, {
        type: 'error',
        timeout: 12000,
        showProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        backdrop: 0.2,
      });
    },

    async _post(url, payload, config, contextTitle) {
      try {
        return await axios.post(url, payload, config);
      } catch (err) {
        this._showErrorAlert(err, contextTitle || 'Order not saved');
        throw err;
      }
    },

    async _patch(url, payload, config, contextTitle) {
      try {
        return await axios.patch(url, payload, config);
      } catch (err) {
        this._showErrorAlert(err, contextTitle || 'Order not saved');
        throw err;
      }
    },

    // optional - short message extractor used by older toasts
    _errMsg(err) {
      const r = err?.response;
      if (typeof r?.data === 'string') return r.data;
      if (r?.data?.message) return r.data.message;
      if (r?.data?.error) return r.data.error;
      return err?.message || 'Network or server error.';
    },

    // ---------- step focus helper ----------
    async _focusPositionsStep() {
      // make positions & rates active
      for (const [k] of Object.entries(this.steps)) {
        this.$store.state.orders.order_steps[k].active = (k === 'positions_rates');
      }
      await this.$nextTick();
      // scroll to it (best-effort)
      try {
        const idx = Object.keys(this.steps).indexOf('positions_rates');
        const containers = this.$el.querySelectorAll('.order_steps .step');
        containers[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {}
    },

    // ---------- salary enforcement (pre-flight, before any POST) ----------
    _getOrderTypeString() {
      const t = this.steps?.basic_info?.data?.type;
      return typeof t === 'string' ? t : (t?.value || '');
    },
    _isPermanentType() { return /permanent/i.test(this._getOrderTypeString()); },
    _isInternalType()  { return /internal/i.test(this._getOrderTypeString()); },
    _shouldEnforceSalary() {
      // Business rule from you: enforce if Internal OR Permanent
      return this._isPermanentType() || this._isInternalType();
    },
    _preflightValidateSalary() {
      if (!this._shouldEnforceSalary()) return true;

      const pr = this.steps.positions_rates?.data;
      if (!pr) return true;

      const errs = [];
      const num = pr.number_of_positions || 0;

      for (let p = 1; p <= num; p++) {
        const pos = pr.position?.[p];
        if (!pos) continue;

        const locations = Array.isArray(pos.locations) ? pos.locations : [];
        locations.forEach((locName, sIdx) => {
          const spec = pos.location_specs?.find(ls => ls.location === locName);
          const info = spec?.position_info;

          const d = info?.perm_details;
          const hasStart = !!d?.salary_range_start;
          const hasEnd   = !!d?.salary_range_end;

          if (!(hasStart && hasEnd)) {
            const posLabel = pos.job_title?.job_title || `Position ${p}`;
            errs.push(
                `${posLabel} @ ${locName || 'location'}: ` +
                `${hasStart ? '' : 'Salary Range Start missing'}${(!hasStart && !hasEnd) ? ' and ' : ''}${hasEnd ? '' : 'Salary Range End missing'}`
            );
          }
        });
      }

      if (errs.length) {
        const fakeErr = {
          response: {
            data: {
              message: 'Salary range is required for Internal or Permanent orders.',
              errors: errs.reduce((acc, line, i) => {
                acc[`item_${i + 1}`] = [line]; return acc;
              }, {})
            }
          }
        };
        this._showErrorAlert(fakeErr, 'Missing salary range');
        this._focusPositionsStep();
        return false;
      }

      return true;
    },

    reset_order() {
      this.$snotify.confirm('', '', {
        closeOnClick: false,
        position: 'centerCenter',
        backdrop: 0.5,
        html:
            '<div class="snotifyToast__title">Reset Order</div>' +
            '<div class="snotifyToast__body">' +
            'Are you sure that you want to clear and reset this order?' +
            '<div class="snotify-icon snotify-icon--confirm"><i class="fas fa-2x fa-exclamation-triangle"></i></div>' +
            '</div>',
        buttons: [
          {
            text: 'Ok',
            action: (toast) => {
              this.$snotify.remove(toast.id);
              this.$store.commit('RESET_ORDER_STEPS');
              this.$tabs.refresh('/orders/create', true);
            },
          },
          { text: 'Cancel', action: (toast) => this.$snotify.remove(toast.id) },
        ],
      });
    },

    select_step(step_key, active) {
      if (!active) {
        for (const [key] of Object.entries(this.$data.steps)) {
          this.$store.state.orders.order_steps[key].active = key === step_key;
        }
      }
    },

    async save_order() {
      try {
        const ok = await this.save_order_2(); // true on success, false on error
        if (!ok) return;

        this.$snotify?.success('Order saved successfully.', 'Success', {
          timeout: 5000,
          showProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
        });

        this.$store.commit('RESET_ORDER_STEPS');

        await this.$nextTick();
        this.$tabs.close({
          path: this.$router.currentRoute.fullPath,
          to: '/orders/awaiting_signature',
        });
        this.$tabs.refresh('/orders/awaiting_signature', true);

        // GeoTech toggles - non-blocking
        const reqs = this.orders
            .filter(o => o.geoTech)
            .map(o =>
                axios.post(this.crmApiPath + 'order/job/geotech', {
                  order_job_id: o.orderID,
                  state: true,
                })
            );
        Promise.allSettled(reqs).then(results => {
          const fails = results.filter(r => r.status === 'rejected').length;
          if (fails > 0) {
            this.$snotify?.warning(
                `${fails} GeoTech toggle${fails > 1 ? 's' : ''} failed - you can retry from the order screen.`,
                'Partial save',
                { timeout: 6000, showProgressBar: true }
            );
          }
        });
      } catch (err) {
        console.error('save_order wrapper failed:', err);
        this.$snotify?.error('Unexpected error while saving order.', 'Order not saved', {
          timeout: 8000,
          showProgressBar: true,
        });
      }
    },

    async save_order_2() {
      this.saving = true;
      try {
        // >>> PRE-FLIGHT: block early if salary range missing for Internal OR Permanent
        if (!this._preflightValidateSalary()) {
          return false; // abort BEFORE any POST
        }

        // 1 - prepare order payload.
        const payroll_solution = this.steps.basic_info.data.payroll_solution
            ? this.steps.basic_info.data.payroll_solution.value
            : null;

        const order = {
          hidden: true,
          date_of_order:
              this.steps.basic_info.data.date_of_order + ' ' + this.$moment.utc().format('HH:mm:ss'),
          consultant_id: this.steps.basic_info.data.consultant.id,
          company_id: this.steps.basic_info.data.company.id,
          order_type: this.steps.basic_info.data.type.value,
          order_status: this.steps.basic_info.data.status,
          order_gm_solution: payroll_solution,
          job_contact_id: this.steps.basic_info.data.job_contact.id,
          invoice_address_id: this.steps.timesheet_authorisation.data.invoice_address.id,
          days_to_pay_from_invoice: this.steps.timesheet_authorisation.data.days_to_pay_from_invoice,
          accounts_contact_id: this.steps.timesheet_authorisation.data.accounts_contact.id,
          authorise_terms_contact_id: this.steps.compliance_terms.data.auth_terms_contact.id,
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id,
        };

        if (payroll_solution === 'Project Managed Labour Solutions') {
          order.gm_project_manager_id = this.steps.basic_info.data.project_manager.id;
        }

        // 2 - create order.
        const { data: orderId } = await this._post(
            this.crmApiPath + 'order',
            order,
            null,
            'Failed creating order'
        );

        // 3 - loop positions and locations.
        let posRef = 'A';
        const locTasks = [];

        for (let p = 1; p <= this.steps.positions_rates.data.number_of_positions; p++) {
          let siteRef = 'A';
          const position = this.steps.positions_rates.data.position[p];

          for (let s = 0; s < position.locations.length; s++) {
            const locationName = position.locations[s];

            locTasks.push(
                (async () => {
                  const positionInfo = position.location_specs.find(ls => ls.location === locationName).position_info;

                  const orderRef = 'CCO' + orderId + '-' + posRef + '' + siteRef;
                  const orderPosRef = posRef;
                  const orderSiteRef = siteRef;
                  const defaultTsAuth = this.steps.timesheet_authorisation.data.ts_submission_auth_type !== 'Order';

                  // b) order_job
                  const order_job = {
                    order_id: orderId,
                    order_ref: orderRef,
                    workers_required: positionInfo.quantity,
                    timesheet_auth_type: this.steps.timesheet_authorisation.data.ts_submission_auth_type,
                    client_po_ref: this.steps.compliance_terms.data.terms_business[p].client_ref_po,
                    amendments_to_terms: this.steps.compliance_terms.data.terms_business[p].amendments_to_terms,
                    default_ts_auth: defaultTsAuth,
                    log_by_type: 'App\\Models\\Users\\User',
                    log_by_id: this.$store.state.auth.me.id,
                  };
                  const { data: orderJobId } = await this._post(
                      this.crmApiPath + 'order/job',
                      order_job,
                      null,
                      'Failed creating order job'
                  );

                  // c) progress
                  const order_progress = {
                    order_job_id: orderJobId,
                    order_progress_status: 'Saved',
                    order_required_manager_auth: false,
                    date_time_sent_to_client: this.$moment.utc().format('YYYY-MM-DD HH:mm:ss'),
                    order_signed: false,
                    log_by_type: 'App\\Models\\Users\\User',
                    log_by_id: this.$store.state.auth.me.id,
                  };
                  await this._post(
                      this.crmApiPath + 'order/progress',
                      order_progress,
                      null,
                      'Failed creating order progress'
                  );

                  // d) declarations and site info.
                  const oLoc = this.steps.job_locations.data.site.find(a => a.short_site_name === locationName);

                  this.orders.push({
                    geoTech: oLoc.geoTechEnabled || false,
                    orderID: orderJobId,
                    shortName: oLoc.short_site_name,
                  });

                  // declarations - aggregate failures into one alert to avoid spam
                  if (Array.isArray(oLoc.rams) && oLoc.rams.length > 0) {
                    const declResults = await Promise.allSettled(
                        oLoc.rams.map(ram => {
                          const formData = new FormData();
                          formData.append('order_job_id', orderJobId);
                          formData.append('title', ram.title || '');
                          formData.append('description', ram.description || '');
                          formData.append('company_required', ram.company_required ? '1' : '0');
                          formData.append('worker_required', ram.worker_required ? '1' : '0');
                          if (ram.link) formData.append('link', ram.link);
                          if (ram.file) formData.append('file', ram.file);
                          return axios.post(this.crmApiPathV2 + 'common/declarations', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                        })
                    );
                    const failed = declResults.filter(r => r.status === 'rejected');
                    if (failed.length) {
                      // show one combined alert
                      const fakeErr = { response: { data: { message: 'Saving declarations failed', errors: {} } } };
                      failed.forEach((r, idx) => {
                        const lines = this._extractErrorLines(r.reason);
                        fakeErr.response.data.errors[`declaration_${idx + 1}`] = lines.join(' | ');
                      });
                      this._showErrorAlert(fakeErr, 'Saving declarations failed');
                      // continue anyway
                    }
                  }

                  const vat =
                      (oLoc.site_vat_treatment && 'site_vat_treatment' in oLoc)
                          ? parseFloat(String(oLoc.site_vat_treatment.value).replace(/%/g, ''))
                          : 20;

                  const order_location = {
                    order_job_id: orderJobId,
                    order_location_ref: orderSiteRef,
                    short_site_name: oLoc.short_site_name,
                    job_address_building: oLoc.address.address_building,
                    job_address_street: oLoc.address.address_street,
                    job_address_locality: oLoc.address.address_locality,
                    job_address_city_town: oLoc.address.address_city_town,
                    job_address_county: oLoc.address.address_county,
                    job_address_postcode: oLoc.address.address_postcode,
                    job_address_full: this.calc_nice_address(
                        oLoc.address.address_building,
                        oLoc.address.address_street,
                        oLoc.address.address_locality,
                        oLoc.address.address_city_town,
                        oLoc.address.address_county,
                        oLoc.address.address_postcode
                    ),
                    site_main_contractor: oLoc.site_main_contractor,
                    site_vat_treatment: vat,
                    reverse_charge: oLoc.reverseCharge ? 1 : 0,
                    use_job_number: oLoc.use_job_number ? 1 : 0,
                    customer_job_number: oLoc.customer_job_number,
                    log_by_type: 'App\\Models\\Users\\User',
                    log_by_id: this.$store.state.auth.me.id,
                  };
                  await this._post(
                      this.crmApiPath + 'order/location',
                      order_location,
                      null,
                      'Failed saving site details'
                  );

                  // e) position
                  const drceReq = this.notEmptyObject(this.steps.compliance_terms.data.drce);
                  const order_position = {
                    order_job_id: orderJobId,
                    order_position_ref: orderPosRef,
                    sector: position.job_title.sector_name,
                    job_title: position.job_title.job_title,
                    start_date: positionInfo.start_date,
                    end_date: positionInfo.end_date,
                    reports_to_contact_id: positionInfo.reports_to.id,
                    essential_qualifications: JSON.stringify(positionInfo.essential_qualifications),
                    key_responsibilities: positionInfo.key_responsibilities,
                    job_brief: positionInfo.job_brief,
                    key_skills_experience: positionInfo.key_skills_experience,
                    health_safety_risks: positionInfo.health_and_safety,
                    ppe_tools_required: positionInfo.ppe_tools,
                    additional_assignment_details: positionInfo.additional_details,
                    recruitment_margin_perc: parseFloat(positionInfo.recruitment_margin_perc),
                    drce_required: drceReq,
                    log_by_type: 'App\\Models\\Users\\User',
                    log_by_id: this.$store.state.auth.me.id,
                  };
                  await this._post(
                      this.crmApiPath + 'order/position',
                      order_position,
                      null,
                      'Failed saving position'
                  );

                  // f) shift
                  const position_shift = {
                    order_job_id: orderJobId,
                    work_monday: positionInfo.working_week.includes('work_monday'),
                    work_tuesday: positionInfo.working_week.includes('work_tuesday'),
                    work_wednesday: positionInfo.working_week.includes('work_wednesday'),
                    work_thursday: positionInfo.working_week.includes('work_thursday'),
                    work_friday: positionInfo.working_week.includes('work_friday'),
                    work_saturday: positionInfo.working_week.includes('work_saturday'),
                    work_sunday: positionInfo.working_week.includes('work_sunday'),
                    work_bank_holidays: positionInfo.working_week.includes('work_bank_holiday'),
                    start_time: positionInfo.start_time,
                    end_time: positionInfo.end_time,
                    lunch_break_length: positionInfo.lunch_length,
                    lunch_break_paid: positionInfo.lunch_paid,
                    total_hours_per_week: positionInfo.total_hours,
                    log_by_type: 'App\\Models\\Users\\User',
                    log_by_id: this.$store.state.auth.me.id,
                  };
                  await this._post(
                      this.crmApiPath + 'order/position_shift',
                      position_shift,
                      null,
                      'Failed saving shift'
                  );

                  // g) rates
                  if (positionInfo.number_of_rates && positionInfo.rate) {
                    const rateTasks = [];
                    for (let r = 1; r <= positionInfo.number_of_rates; r++) {
                      const pr = {
                        order_job_id: orderJobId,
                        rate_type: positionInfo.rate[r].type.value,
                        pay_type: positionInfo.rate[r].pay_type.value,
                        pay_amount: positionInfo.rate[r].pay_amount,
                        admin_fee_type: positionInfo.rate[r].admin_fee_type.value,
                        admin_fee_amount: positionInfo.rate[r].admin_fee,
                        retention_percentage: positionInfo.rate[r].retention_percentage,
                        log_by_type: 'App\\Models\\Users\\User',
                        log_by_id: this.$store.state.auth.me.id,
                      };
                      rateTasks.push(
                          this._post(this.crmApiPath + 'order/position_rate', pr, null, 'Failed saving rate')
                      );
                    }
                    await Promise.all(rateTasks);
                  }

                  // h) timesheet auths
                  const tsType = this.steps.timesheet_authorisation.data.ts_submission_auth_type;
                  const postTs = async (rows, ctx) => {
                    const tasks = rows.map(row => {
                      const payload = {
                        company_id: row.company_id ?? row.contact?.company_id,
                        order_job_id: orderJobId,
                        submission_level: row.submission_level,
                        contact_id: row.id ?? row.contact?.id,
                        log_by_type: 'App\\Models\\Users\\User',
                        log_by_id: this.$store.state.auth.me.id,
                      };
                      if (this.steps.basic_info.data.type.value !== 'Permanent') {
                        return this._post(this.crmApiPath + 'timesheet_authorisation', payload, null, ctx);
                      }
                      return Promise.resolve();
                    });
                    await Promise.all(tasks);
                  };

                  if (tsType === 'Order') {
                    await postTs(this.steps.timesheet_authorisation.data.timesheet_auth.authorising_ts, 'Failed saving timesheet authoriser');
                    await postTs(this.steps.timesheet_authorisation.data.timesheet_auth.submit_ts, 'Failed saving timesheet submitter');
                  } else if (tsType === 'Default') {
                    await postTs(this.steps.timesheet_authorisation.data.timesheet_auth[1].auth_ts, 'Failed saving timesheet authoriser');
                    await postTs(this.steps.timesheet_authorisation.data.timesheet_auth[1].submit_ts, 'Failed saving timesheet submitter');
                  }

                  // i) perm details post (only for actual Permanent type)
                  if (this._isPermanentType() && positionInfo.perm_details) {
                    const pd = {
                      order_job_id: orderJobId,
                      salary_range_start: positionInfo.perm_details.salary_range_start,
                      salary_range_end: positionInfo.perm_details.salary_range_end,
                      bonus: positionInfo.perm_details.bonus,
                      bonus_how_calculated: positionInfo.perm_details.bonus_how_calculated,
                      bonus_when_paid: positionInfo.perm_details.bonus_when_paid,
                      bonus_amount_per_annum: positionInfo.perm_details.bonus_amount_per_annum,
                      bonus_included_in_charge: positionInfo.perm_details.bonus_included_in_charge,
                      car_allowance: positionInfo.perm_details.car_allowance,
                      car_allowance_make_model: positionInfo.perm_details.car_allowance_make_model,
                      car_allowance_amount_per_annum: positionInfo.perm_details.car_allowance_amount_per_annum,
                      car_allowance_included_in_charge: positionInfo.perm_details.car_allowance_included_in_charge,
                      pension_contribution_above_standard: positionInfo.perm_details.pension_contribution_above_standard,
                      pension_percentage: positionInfo.perm_details.pension_percentage,
                      pension_included_in_charge: positionInfo.perm_details.pension_included_in_charge,
                      health_care: positionInfo.perm_details.health_care,
                      health_care_type: positionInfo.perm_details.health_care_type,
                      health_care_amount_per_annum: positionInfo.perm_details.health_care_amount_per_annum,
                      health_care_included_in_charge: positionInfo.perm_details.health_care_included_in_charge,
                      any_other_benefits: positionInfo.perm_details.any_other_benefits,
                      any_other_benefits_description: positionInfo.perm_details.any_other_benefits_description,
                      any_other_benefits_amount_per_annum: positionInfo.perm_details.any_other_benefits_amount_per_annum,
                      any_other_benefits_included_in_charge: positionInfo.perm_details.any_other_benefits_included_in_charge,
                      log_by_type: 'App\\Models\\Users\\User',
                      log_by_id: this.$store.state.auth.me.id,
                    };
                    await this._post(
                        this.crmApiPath + 'order/position_perm_detail',
                        pd,
                        null,
                        'Permanent position details failed'
                    );
                  }

                  // j) moo
                  if (this.steps.basic_info.data.type.value === 'Contract') {
                    const order_moo = {
                      order_job_id: orderJobId,
                      subject_to_sdc: this.steps.compliance_terms.data.moo.sdc,
                      worker_decline_job_movement: this.steps.compliance_terms.data.moo.worker_decline_job_movement,
                      end_user_obliged_to_offer_work: this.steps.compliance_terms.data.moo.end_user_offer_further_work,
                      worker_obliged_to_accept_work: this.steps.compliance_terms.data.moo.worker_accept_further_work,
                      worker_choose_how_work_performed: this.steps.compliance_terms.data.moo.how_work_performed,
                      worker_responsible_for_work: this.steps.compliance_terms.data.moo.responsible_work_standard,
                      worker_carry_financial_risk: this.steps.compliance_terms.data.moo.carry_financial_risk,
                      log_by_type: 'App\\Models\\Users\\User',
                      log_by_id: this.$store.state.auth.me.id,
                    };
                    await this._post(this.crmApiPath + 'order/moo', order_moo, null, 'Failed saving MOO');
                  }

                  // k) publish info
                  const pubJt = this.steps.publish_information.data[p - 1];
                  const pubI = pubJt.find(a => a.location === locationName);
                  const order_publish_info = {
                    order_job_id: orderJobId,
                    order_published: pubI.order_published,
                    publish_text: pubI.publish_text,
                    log_by_type: 'App\\Models\\Users\\User',
                    log_by_id: this.$store.state.auth.me.id,
                  };
                  await this._post(
                      this.crmApiPath + 'order/publish_info',
                      order_publish_info,
                      null,
                      'Failed saving publish info'
                  );
                })()
            );

            siteRef = String.fromCharCode(siteRef.charCodeAt(0) + 1);
          }

          await Promise.all(locTasks.splice(0));
          posRef = String.fromCharCode(posRef.charCodeAt(0) + 1);
        }

        // 4 - send email.
        await this._post(
            this.crmApiPath + 'order/send_order_email',
            {
              status: this.steps.basic_info.data.status,
              order_id: orderId,
              consultant_name:
                  this.steps.basic_info.data.consultant.first_name +
                  ' ' +
                  this.steps.basic_info.data.consultant.surname,
              contact_first_name: this.steps.compliance_terms.data.auth_terms_contact.person.first_name,
              contact_surname: this.steps.compliance_terms.data.auth_terms_contact.person.surname,
              contact_email: this.steps.compliance_terms.data.auth_terms_contact.emails.work_email,
            },
            null,
            'Failed sending order email'
        );

        // 5 - unhide order.
        await this._patch(
            this.crmApiPath + 'order/' + orderId,
            { hidden: false },
            null,
            'Failed finalising order'
        );

        return true;
      } catch (err) {
        // detailed alert already shown by _post/_patch.
        // belt-and-braces for unexpected errors without a response.
        if (!err?.response) {
          this._showErrorAlert(err, 'Order not saved');
        }
        return false;
      } finally {
        this.saving = false;
      }
    },
  },
  beforePageLeave(tab, type) {
    this.$store.commit('RESET_ORDER_STEPS');
  },
};
</script>
